// Two-constant Kubelka-Munk optics, evaluated per spectral band.
//
// A paint layer is described by its total absorption KX and scattering SX
// (coefficient times thickness, summed over the pigments in the layer). Layers
// are composited bottom-up: each layer's reflectance becomes the substrate (Rg)
// of the next. Formulas follow Kubelka (1948) for a finite layer over a
// substrate, rearranged so that SX -> 0 (pure glaze, Beer-Lambert) and
// KX -> 0 (pure scatterer) remain numerically stable.

import { BAND_COUNT } from './ColourConversion.js';

// q * coth(q), with its series expansion near zero.
function qCothQ(q) {
  if (q < 1e-3) return 1 + (q * q) / 3;
  if (q > 20) return q;
  return q / Math.tanh(q);
}

// Reflectance of a layer with absorption KX and scattering SX over a substrate of reflectance rg.
export function kmLayerReflectance(KX, SX, rg) {
  const k = KX > 0 ? KX : 0;
  const s = SX > 0 ? SX : 0;
  if (k < 1e-9 && s < 1e-9) return rg;
  const q = Math.sqrt(k * k + 2 * k * s);
  const qc = qCothQ(q);
  if (s < 1e-7) {
    // Pure absorber: two passes through the layer.
    return rg * Math.exp(-2 * k);
  }
  const numerator = s - rg * (s + k - qc);
  const denominator = s + k - rg * s + qc;
  const r = numerator / denominator;
  return r < 0 ? 0 : r > 1 ? 1 : r;
}

// Reflectance of an optically infinite (fully hiding) layer, from K/S.
export function kmInfiniteReflectance(K, S) {
  if (S <= 1e-9) return 0;
  const ratio = K / S;
  return 1 + ratio - Math.sqrt(ratio * ratio + 2 * ratio);
}

// Saunderson correction: converts internal (KM) reflectance to what leaves a
// glossy dielectric surface, excluding the first-surface specular reflection
// k1, which the lighting stage renders separately as specular light.
export const SAUNDERSON_K1 = 0.04; // normal-incidence Fresnel for n ~= 1.5
export const SAUNDERSON_K2 = 0.6; // internal diffuse reflection at the surface

export function saundersonBody(rInternal) {
  return ((1 - SAUNDERSON_K1) * (1 - SAUNDERSON_K2) * rInternal) / (1 - SAUNDERSON_K2 * rInternal);
}

// Inverse of saundersonBody, used when deriving K/S from a measured or chosen display reflectance.
export function saundersonInverse(rObserved) {
  const a = (1 - SAUNDERSON_K1) * (1 - SAUNDERSON_K2);
  return rObserved / (a + SAUNDERSON_K2 * rObserved);
}

// Accumulates a layer's KX/SX spectra from pigment amounts.
// amounts: per-slot amount (concentration x thickness); slots: array of pigment
// objects with Float32Array K and S spectra (per unit amount).
export function accumulateLayer(amounts, slots, outK, outS, offset = 0, stride = 1) {
  for (let band = 0; band < BAND_COUNT; band += 1) {
    outK[band] = 0;
    outS[band] = 0;
  }
  for (let slot = 0; slot < slots.length; slot += 1) {
    const amount = amounts[offset + slot * stride];
    if (!(amount > 1e-7)) continue;
    const pigment = slots[slot];
    if (!pigment) continue;
    for (let band = 0; band < BAND_COUNT; band += 1) {
      outK[band] += amount * pigment.K[band];
      outS[band] += amount * pigment.S[band];
    }
  }
}

// Composites a layer (KX, SX spectra) over the substrate spectrum in place.
export function compositeLayerInPlace(substrate, KX, SX) {
  for (let band = 0; band < BAND_COUNT; band += 1) {
    substrate[band] = kmLayerReflectance(KX[band], SX[band], substrate[band]);
  }
}

// Convenience for tests and swatches: reflectance spectrum of a pigment mixture
// (amounts) at thickness X over a substrate spectrum.
export function mixtureReflectance(slots, amounts, substrate, thickness = 1) {
  const KX = new Float32Array(BAND_COUNT);
  const SX = new Float32Array(BAND_COUNT);
  accumulateLayer(amounts.map((a) => a * thickness), slots, KX, SX);
  const out = Float32Array.from(substrate);
  compositeLayerInPlace(out, KX, SX);
  return out;
}
